import axios from 'axios';
import { debug, error } from '@/utils/logger';

function sanitizeFileName(name: string): string {
  return name.trim().replace(/\s+/g, '_').replace(/[()]/g, '').replace(/[^a-zA-Z0-9._-]/g, '');
}

type ResizeOpts = {
  maxWidth: number;
  maxHeight: number;
  fit: 'cover' | 'inside';
  quality: number;
};

type UploadCategory =
  | 'hero'
  | 'profile_large'
  | 'profile_small'
  | 'post_cover'
  | 'place'
  | 'recommendation'
  | 'card'
  | 'itinerary_import';

type PresignUploadResponse = {
  uploadUrl: string;
  objectUrl: string;
  key: string;
  headers?: Record<string, string>;
  expiresIn?: number;
};

type DeleteMediaResponse = {
  status?: string;
  data?: {
    deleted?: boolean;
    key?: string;
  };
};

type SignedUrlDebugDetails = {
  origin?: string;
  pathname?: string;
  query?: {
    algorithm?: string;
    credential?: string;
    date?: string;
    expires?: string;
    signedHeaders?: string;
  };
};

const ALLOWED_UPLOAD_CONTENT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

function getApiBaseUrl(): string {
  const apiBaseUrl = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
  if (!apiBaseUrl) {
    throw new Error('API base URL not configured. Set NEXT_PUBLIC_APP_API_BASE_URL.');
  }
  return apiBaseUrl;
}

function extensionFromContentType(contentType: string): string {
  switch (contentType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    default:
      return 'jpg';
  }
}

function normalizeUploadContentType(contentType: string): string {
  const normalized = contentType.trim().toLowerCase();
  if (!ALLOWED_UPLOAD_CONTENT_TYPES.has(normalized)) {
    throw new Error(
      'Unsupported image type. Allowed types: image/jpeg, image/png, image/webp, image/heic, image/heif.',
    );
  }
  return normalized;
}

function categoryFromPath(path?: string): UploadCategory {
  if (!path) return 'hero';
  if (path.startsWith('public/post_images/')) return 'post_cover';
  if (path.startsWith('public/post_location_images/')) return 'place';
  if (path.startsWith('public/recommendation_images/')) return 'recommendation';
  if (path.startsWith('public/card_images/')) return 'card';
  if (path.startsWith('public/profile_images/large/')) return 'profile_large';
  if (path.startsWith('public/profile_images/small/')) return 'profile_small';
  if (path.startsWith('public/hero_images/')) return 'hero';
  return 'hero';
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const responseMessage = (err.response?.data as { message?: string } | undefined)?.message;
    if (responseMessage) return responseMessage;
    if (err.response?.status === 429) {
      return 'Upload is temporarily rate limited. Please try again shortly.';
    }
    if (err.response?.status === 413) {
      return 'Image is too large. Please choose a smaller file.';
    }
    if (err.response?.status === 503) {
      return 'Upload service is temporarily unavailable. Please try again later.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function getSignedUrlDebugDetails(signedUrl: string): SignedUrlDebugDetails {
  try {
    const parsed = new URL(signedUrl);
    const query = parsed.searchParams;
    return {
      origin: parsed.origin,
      pathname: parsed.pathname,
      query: {
        algorithm: query.get('X-Amz-Algorithm') ?? undefined,
        credential: query.get('X-Amz-Credential') ?? undefined,
        date: query.get('X-Amz-Date') ?? undefined,
        expires: query.get('X-Amz-Expires') ?? undefined,
        signedHeaders: query.get('X-Amz-SignedHeaders') ?? undefined,
      },
    };
  } catch {
    return {};
  }
}

function extractS3ErrorCode(responseText: string): string | undefined {
  const match = responseText.match(/<Code>([^<]+)<\/Code>/);
  return match?.[1];
}

async function requestPresignUpload(params: {
  category: UploadCategory;
  contentType: string;
  extension: string;
  contentLength: number;
  ownerId?: string;
  targetPath?: string;
}): Promise<PresignUploadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/media/presign-upload`;
  debug('[clientUpload] requesting presign upload', {
    category: params.category,
    contentType: params.contentType,
    extension: params.extension,
    contentLength: params.contentLength,
    targetPath: params.targetPath,
  });
  const response = await axios.post<PresignUploadResponse>(url, params);
  debug('[clientUpload] received presign upload response', {
    objectUrl: response.data.objectUrl,
    key: response.data.key,
    expiresIn: response.data.expiresIn,
    uploadHeaders: response.data.headers,
    uploadUrlDetails: getSignedUrlDebugDetails(response.data.uploadUrl),
  });
  return response.data;
}

async function putToSignedUrl(
  signedUrl: string,
  body: Blob,
  contentType: string,
  uploadHeaders?: Record<string, string>,
): Promise<void> {
  const headers = new Headers(uploadHeaders || {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', contentType);
  }

  const requestHeaders = Object.fromEntries(headers.entries());
  debug('[clientUpload] uploading file to signed URL', {
    contentType,
    contentLength: body.size,
    requestHeaders,
    uploadUrlDetails: getSignedUrlDebugDetails(signedUrl),
  });

  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers,
    body,
  });

  if (!response.ok) {
    const responseText = await response.text();
    const s3ErrorCode = extractS3ErrorCode(responseText);
    debug('[clientUpload] signed URL upload failed', {
      status: response.status,
      statusText: response.statusText,
      s3ErrorCode,
      responseText,
      uploadUrlDetails: getSignedUrlDebugDetails(signedUrl),
    });
    throw new Error(
      `Upload failed with status ${response.status}${s3ErrorCode ? ` (${s3ErrorCode})` : ''}`,
    );
  }

  debug('[clientUpload] signed URL upload succeeded', {
    status: response.status,
    uploadUrlDetails: getSignedUrlDebugDetails(signedUrl),
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

async function resizeToJpegBlob(file: File, opts: ResizeOpts): Promise<Blob> {
  const img = await loadImage(file);
  const { maxWidth, maxHeight, fit, quality } = opts;

  let targetW: number;
  let targetH: number;
  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;

  if (fit === 'cover') {
    if (img.width <= maxWidth && img.height <= maxHeight) {
      targetW = img.width;
      targetH = img.height;
    } else {
      targetW = maxWidth;
      targetH = maxHeight;
      const srcRatio = img.width / img.height;
      const tgtRatio = targetW / targetH;
      if (srcRatio > tgtRatio) {
        sw = img.height * tgtRatio;
        sx = (img.width - sw) / 2;
      } else {
        sh = img.width / tgtRatio;
        sy = (img.height - sh) / 2;
      }
    }
  } else {
    const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
    targetW = Math.max(1, Math.round(img.width * ratio));
    targetH = Math.max(1, Math.round(img.height * ratio));
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/jpeg',
      quality,
    );
  });
}

async function uploadViaPresign(params: {
  category: UploadCategory;
  body: Blob;
  contentType: string;
  ownerId?: string;
  targetPath?: string;
}): Promise<string> {
  const contentType = normalizeUploadContentType(params.contentType);
  const extension = extensionFromContentType(contentType);
  const presign = await requestPresignUpload({
    category: params.category,
    contentType,
    extension,
    contentLength: params.body.size,
    ownerId: params.ownerId,
    targetPath: params.targetPath,
  });
  await putToSignedUrl(presign.uploadUrl, params.body, contentType, presign.headers);
  return presign.objectUrl;
}

export async function clientUploadProfileImage(
  file: File,
  userId?: string,
): Promise<{ large_avatar_url: string; small_avatar_url: string }> {
  const timestamp = Date.now();
  const fileName = userId
    ? `profile_image_${userId}_${timestamp}.jpg`
    : `${timestamp}_${sanitizeFileName(file.name)}`;

  const [largeBlob, smallBlob] = await Promise.all([
    resizeToJpegBlob(file, { maxWidth: 1000, maxHeight: 1000, fit: 'cover', quality: 0.9 }),
    resizeToJpegBlob(file, { maxWidth: 300, maxHeight: 300, fit: 'cover', quality: 0.85 }),
  ]);

  const largeKey = `public/profile_images/large/${fileName}`;
  const smallKey = `public/profile_images/small/${fileName}`;

  const [large_avatar_url, small_avatar_url] = await Promise.all([
    uploadViaPresign({
      category: 'profile_large',
      body: largeBlob,
      contentType: 'image/jpeg',
      ownerId: userId,
      targetPath: largeKey,
    }),
    uploadViaPresign({
      category: 'profile_small',
      body: smallBlob,
      contentType: 'image/jpeg',
      ownerId: userId,
      targetPath: smallKey,
    }),
  ]);

  debug('[clientUpload] profile image uploaded', { large_avatar_url, small_avatar_url });
  return { large_avatar_url, small_avatar_url };
}

export async function clientUploadHeroImage(
  file: File,
  userId?: string,
  path?: string,
): Promise<{ hero_image_url: string }> {
  const timestamp = Date.now();
  const fileName = userId
    ? `hero_image_${userId}_${timestamp}.jpg`
    : `${timestamp}_${sanitizeFileName(file.name)}`;

  const heroBlob = await resizeToJpegBlob(file, {
    maxWidth: 2000,
    maxHeight: 2000,
    fit: 'inside',
    quality: 0.9,
  });

  const heroKey = path || `public/hero_images/large/${fileName}`;
  try {
    const hero_image_url = await uploadViaPresign({
      category: categoryFromPath(heroKey),
      body: heroBlob,
      contentType: 'image/jpeg',
      ownerId: userId,
      targetPath: heroKey,
    });
    debug('[clientUpload] hero image uploaded', { hero_image_url });
    return { hero_image_url };
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to upload hero image.'));
  }
}

export async function clientUploadGenericImage(
  file: File,
  path?: string,
): Promise<{ url: string }> {
  const timestamp = Date.now();
  const sanitizedFileName = `${timestamp}_${sanitizeFileName(file.name)}`;
  const key = path ? `${path}${sanitizedFileName}` : `public/profile_images/large/${sanitizedFileName}`;
  const contentType = file.type || '';
  try {
    const url = await uploadViaPresign({
      category: categoryFromPath(key),
      body: file,
      contentType,
      targetPath: key,
    });
    return { url };
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to upload image.'));
  }
}

const IMPORT_DOC_CONTENT_TYPES = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
} as const;

const IMPORT_FILE_MAX_BYTES = 20 * 1024 * 1024;

function resolveImportDocumentType(file: File): 'pdf' | 'docx' | null {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf') || file.type === IMPORT_DOC_CONTENT_TYPES.pdf) {
    return 'pdf';
  }
  if (name.endsWith('.docx') || file.type === IMPORT_DOC_CONTENT_TYPES.docx) {
    return 'docx';
  }
  if (name.endsWith('.doc')) {
    throw new Error('Legacy .doc files are not supported. Save as .docx or PDF and try again.');
  }
  return null;
}

export async function clientUploadImportDocument(
  file: File,
): Promise<{ key: string; sourceType: 'pdf' | 'docx' }> {
  const sourceType = resolveImportDocumentType(file);
  if (!sourceType) {
    throw new Error('Please choose a PDF or Word (.docx) file.');
  }
  if (file.size > IMPORT_FILE_MAX_BYTES) {
    throw new Error('File is too large. Maximum size is 20 MB.');
  }

  const contentType = IMPORT_DOC_CONTENT_TYPES[sourceType];
  const extension = sourceType;
  const presign = await requestPresignUpload({
    category: 'itinerary_import',
    contentType,
    extension,
    contentLength: file.size,
  });

  await putToSignedUrl(presign.uploadUrl, file, contentType, presign.headers);
  return { key: presign.key, sourceType };
}

export async function clientDeleteImage(imageUrl: string): Promise<void> {
  try {
    const parsed = new URL(imageUrl);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      throw new Error('URL must be http or https');
    }
  } catch (parseErr) {
    error('[clientDeleteImage] invalid object URL', { imageUrl, parseErr });
    throw new Error('Invalid image URL. Please try again.');
  }

  try {
    const url = `${getApiBaseUrl()}/api/v1/media/delete`;
    await axios.post<DeleteMediaResponse>(url, { objectUrl: imageUrl });
    debug('[clientDeleteImage] deleted via backend endpoint', { imageUrl });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const responseMessage = (err.response?.data as { message?: string } | undefined)?.message;
      if (responseMessage) {
        throw new Error(responseMessage);
      }
      if (err.response?.status === 400) {
        throw new Error('Delete request was invalid. Please refresh and try again.');
      }
      if (err.response?.status === 403) {
        throw new Error('You do not have permission to delete this image.');
      }
      if (err.response?.status === 429) {
        throw new Error('Delete is temporarily rate limited. Please try again shortly.');
      }
      if (err.response?.status === 503) {
        throw new Error('Delete service is temporarily unavailable. Please try again later.');
      }
    }

    throw new Error('Failed to delete image.');
  }
}
